import {
  handleSubmitMultilingualForm,
  useCreateArtist,
  useGetArtistFormData,
  useUpdateArtist,
} from "@/api/cms";
import {
  ArtistFormProps,
  BibliographyForm,
  BiographyForm,
  CollectionsForm,
  ExhibitionsForm,
  NewArtistForm,
} from "@/components/cms/ArtistForm.tsx";
import { Switch } from "@/components/form-items/AbstractForm";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs.tsx";
import { useToast } from "@/hooks/use-toast.ts";
import {
  constructLocaleDict,
  getMainLanguage,
  multilingualFieldSchema,
  SupportedLocale,
} from "@/i18n";
import CMSLayout from "@/layout/CMSLayout.tsx";
import { artistContentSchema, baseCmsFormSchema } from "@/lib/cms/cms-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const itemFormSchema = baseCmsFormSchema
  .merge(
    artistContentSchema.omit({
      id: true,
    }),
  )
  .optional();

const artistFormSchema = multilingualFieldSchema(itemFormSchema);

type FormData = z.infer<typeof artistFormSchema>;

const ArtistCMS = () => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const create = useCreateArtist();
  const update = useUpdateArtist();

  const { data: artistData } = useGetArtistFormData();

  const defaultValues: FormData = useMemo(
    () =>
      constructLocaleDict((lang) => {
        const artist = artistData?.[lang];
        return {
          ...artist?.content,
          publishedBibliography:
            artist?.content?.publishedBibliography ?? false,
          publishedBiography: artist?.content?.publishedBiography ?? false,
          publishedCollections: artist?.content?.publishedCollections ?? false,
          publishedExhibitions: artist?.content?.publishedExhibitions ?? false,
          published: artist?.published ?? false,
          pageName: artist?.pageName ?? "",
        } satisfies z.infer<typeof itemFormSchema>;
      }),
    [artistData],
  );

  const form = useForm<FormData>({
    resolver: zodResolver(artistFormSchema),
    values: defaultValues,
  });

  const onSubmit = (submittedData: FormData) => {
    Object.keys(form.formState.dirtyFields).forEach((key) => {
      const localeData = submittedData[key as SupportedLocale];
      const baseData = artistData?.[key as SupportedLocale];
      if (!localeData) return;

      const mutation = baseData?.content.id ? update : create;

      const { pageName, published, ...content } = localeData;

      mutation.mutate(
        {
          id: baseData?.content.id ?? "",
          lang: key as SupportedLocale,
          data: {
            pageName,
            published,
            content,
          },
        },
        {
          onSuccess: () => {
            toast({
              title: t("success"),
              description: t("homepageSuccessMessage"),
              duration: 1000,
              variant: "success",
            });
          },
          onError: () => {
            toast({
              title: t("error"),
              description: t("homepageErrorMessage"),
              variant: "error",
              duration: 1000,
            });
          },
        },
      );
    });
  };

  const tabList = useMemo(
    () =>
      [
        {
          label:
            artistData?.[getMainLanguage()]?.pageName ??
            t("cms.artist.tabs.artist"),
          component: NewArtistForm,
          value: "artist",
          publishedSwitch: "published",
        },
        {
          label:
            artistData?.[getMainLanguage()]?.content.biographySectionTitle ??
            t("cms.artist.tabs.biography"),
          component: BiographyForm,
          value: "biography",
          publishedSwitch: "publishedBiography",
        },
        {
          label:
            artistData?.[getMainLanguage()]?.content.collectionsSectionTitle ??
            t("cms.artist.tabs.collections"),
          component: CollectionsForm,
          value: "collections",
          publishedSwitch: "publishedCollections",
        },
        {
          label:
            artistData?.[getMainLanguage()]?.content.exhibitionsSectionTitle ??
            t("cms.artist.tabs.exhibitions"),
          component: ExhibitionsForm,
          value: "exhibitions",
          publishedSwitch: "publishedExhibitions",
        },
        {
          label:
            artistData?.[getMainLanguage()]?.content.bibliographySectionTitle ??
            t("cms.artist.tabs.bibliography"),
          component: BibliographyForm,
          value: "bibliography",
          publishedSwitch: "publishedBibliography",
        },
      ] satisfies {
        label: string;
        value: string;
        publishedSwitch: string;
        component: (props: ArtistFormProps) => JSX.Element;
      }[],
    [t, artistData],
  );

  return (
    <CMSLayout pageName={t("cmsTabArtiste")} formIds={["artist"]}>
      <FormProvider {...form}>
        <Tabs className="w-full" defaultValue="artist" asChild>
          <form
            onSubmit={handleSubmitMultilingualForm(form, artistData, onSubmit)}
            id="artist"
            className="flex flex-col gap-4"
          >
            <TabsList>
              {tabList.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {tabList.map((tab) => {
              const Comp = tab.component;
              return (
                <TabsContent
                  key={tab.value}
                  value={tab.value}
                  className="inline-flex gap-8 w-full data-[state=inactive]:hidden"
                >
                  <DoubleMultilingualField
                    corner={(lang) => (
                      <Switch
                        dispLabel={false}
                        name={`${lang}.${tab.publishedSwitch}`}
                      />
                    )}
                    render={(lang) => <Comp language={lang} />}
                  />
                </TabsContent>
              );
            })}
          </form>
        </Tabs>
      </FormProvider>
    </CMSLayout>
  );
};

export default ArtistCMS;
